import type { OrderFormData, LegData } from "@/components/orders/orderFormTypes";
import type { Tractor } from "@/types/tms";
import type { TractorAssignmentCheck } from "@/lib/truck-eligibility";
import { resolveFormLegs } from "@/lib/order-form-legs";
import { planLegEditRules } from "@/lib/leg-edit-rules";

/**
 * Flattens an order form into the tractor/trip-type pairs that truck
 * eligibility must approve — one pair per leg, using each leg's effective
 * tractor and trip type. A single-leg order yields exactly one pair.
 * Shared by Create Order and Edit Order so both enforce the same rule.
 *
 * The order-level tractor is deliberately not checked on its own: for a
 * multi-leg order in per-leg mode it is only a back-compat mirror of leg 1,
 * and validating it against the order-level trip type would block legitimate
 * per-leg combinations (leg 1 Manual with a manual-only truck, leg 2 SVO with
 * an autonomous truck).
 *
 * Eligibility is a *dispatch-time* rule: it answers "may this truck be sent on
 * this trip?". A stop that has already departed, delivered or been canceled
 * cannot be reassigned anyway, so it is skipped — otherwise editing or closing
 * out a stale order fails with "Eligibility Data Missing" for work that
 * already ran (reported by dispatch, 2026-09-03).
 */
export function buildEligibilityChecks(
  formData: OrderFormData,
  tractors: Tractor[] | undefined,
  ctx: { orderStatus?: unknown } = {},
): TractorAssignmentCheck[] {
  const nameById = new Map((tractors ?? []).map((t) => [t.id, t.name]));
  const legs = resolveFormLegs(formData);
  const multi = legs.length > 1;

  return legs
    .filter((leg) => !!leg.tractorId)
    .filter((leg) => {
      const locks = planLegEditRules({
        legStatus: (leg.leg as (LegData & { driver_status?: unknown }) | undefined)
          ?.driver_status,
        orderStatus: ctx.orderStatus ?? formData.status,
        parentIsRollup: formData.order_type === "multi-leg",
      });
      // `assetsLocked` marks exactly the stops whose truck can no longer be
      // changed (in transit / delivered / canceled / tender received).
      return !locks.assetsLocked;
    })
    .map((leg) => ({
      tractorId: leg.tractorId,
      tripType: leg.tripType,
      tractorLabel: nameById.get(leg.tractorId) ?? null,
      ...(multi ? { context: `Stop ${leg.index + 1}` } : {}),
    }));
}

